import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  KeyRound, Plus, Copy, Check, Trash2, Loader2, Bot, AlertTriangle, Coins, ArrowLeft, Terminal, Ban,
} from 'lucide-react';
import { AgentKey, AGENT_API_URL, MCP_URL, createAgentKey, listAgentKeys, revokeAgentKey } from '../services/agentKeyService';

const ApiKeysView: React.FC = () => {
  const navigate = useNavigate();
  const [keys, setKeys] = useState<AgentKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [reveal, setReveal] = useState<{ name: string; api_key: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = async () => { setLoading(true); try { setKeys(await listAgentKeys()); } catch (e: any) { setErr(e.message); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setCreating(true); setErr(null);
    try { const r = await createAgentKey(name || 'My Agent'); setReveal({ name: r.name, api_key: r.api_key }); setName(''); await load(); }
    catch (e: any) { setErr(e.message || 'Could not create key.'); }
    finally { setCreating(false); }
  };

  const revoke = async (k: AgentKey) => {
    if (!window.confirm(`Revoke "${k.name}"? Apps using it will stop working immediately.`)) return;
    try { await revokeAgentKey(k.id); await load(); } catch (e: any) { setErr(e.message); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-in fade-in duration-300">
      <div className="flex items-center gap-3 border-b border-slate-700 pb-4">
        <button onClick={() => navigate('/settings')} className="p-2 hover:bg-slate-800 rounded-lg text-slate-400 hover:text-white"><ArrowLeft size={20} /></button>
        <div>
          <h1 className="text-xl font-bold text-white flex items-center gap-2"><KeyRound className="text-indigo-400" size={20} /> API keys</h1>
          <p className="text-slate-400 text-sm">Keys for the Tinaney agent API &amp; MCP server.</p>
        </div>
      </div>

      {err && <div className="bg-red-900/20 border border-red-500/30 p-3 rounded-lg text-red-200 text-sm">{err}</div>}

      {/* Endpoints */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-4 space-y-3">
        <h3 className="text-white font-semibold flex items-center gap-2 text-sm"><Terminal size={15} className="text-indigo-400" /> Endpoints</h3>
        <EndpointRow label="Agent API" value={AGENT_API_URL} hint="header: x-api-key: <key>" />
        <EndpointRow label="MCP (hosted)" value={MCP_URL} hint="header: Authorization: Bearer <key>" />
      </div>

      {/* One-time key reveal */}
      {reveal && <RevealBox reveal={reveal} onClose={() => setReveal(null)} />}

      {/* Create */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
        <label className="block text-xs text-slate-400 mb-1.5">Create a new key</label>
        <div className="flex flex-col sm:flex-row gap-2">
          <input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && create()}
            placeholder="Name (e.g. My research bot)"
            className="flex-1 bg-slate-900 border border-slate-600 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-indigo-500" />
          <button onClick={create} disabled={creating}
            className="bg-indigo-600 hover:bg-indigo-500 text-white px-5 py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50">
            {creating ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Create key
          </button>
        </div>
        <p className="text-[11px] text-slate-500 mt-2">New keys start with <b>0 credits</b> — top up from your agent via the <code>TOPUP</code> action (x402/USDC). Up to 10 active keys.</p>
      </div>

      {/* Keys */}
      {loading ? <div className="flex justify-center py-8"><Loader2 className="animate-spin text-indigo-500" /></div> : keys.length === 0 ? (
        <div className="text-center py-10 bg-slate-800/50 rounded-xl border border-dashed border-slate-700">
          <Bot size={36} className="mx-auto text-slate-600 mb-3" />
          <p className="text-slate-400 text-sm">No keys yet. Create one to connect an agent.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {keys.map((k) => (
            <div key={k.id} className={`bg-slate-800 border border-slate-700 rounded-xl p-4 flex flex-wrap items-center gap-3 ${!k.active ? 'opacity-60' : ''}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-white font-medium truncate">{k.name}</span>
                  {!k.active && <span className="text-[10px] bg-slate-700 text-slate-300 px-1.5 py-0.5 rounded flex items-center gap-1"><Ban size={10} /> revoked</span>}
                </div>
                <code className="text-xs text-slate-500 font-mono">{k.key_prefix || 'tinaney_'}…</code>
              </div>
              <div className="flex items-center gap-4 text-xs text-slate-400">
                <span className="flex items-center gap-1" title="Credit balance"><Coins size={13} className="text-yellow-400" /> {k.credit_balance}</span>
                <span title="Requests made">{k.calls} calls</span>
                <span title="Rate limit">{k.rate_limit_per_min}/min</span>
              </div>
              {k.active && (
                <button onClick={() => revoke(k)} title="Revoke" className="p-2 text-slate-400 hover:text-red-400 hover:bg-red-900/20 rounded-lg"><Trash2 size={16} /></button>
              )}
            </div>
          ))}
        </div>
      )}

      <p className="text-xs text-slate-500">
        Actions: <code>RETRIEVE_DATA</code>, <code>CROSS_ANALYSIS</code>, <code>ANALYZE_RESULTS</code>, <code>CREATE_SURVEY</code>,
        <code>BALANCE</code>, <code>TOPUP</code>. See <span className="text-indigo-400">docs/AGENT_API.md</span> for the full reference.
      </p>
    </div>
  );
};

const EndpointRow: React.FC<{ label: string; value: string; hint: string }> = ({ label, value, hint }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard?.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  return (
    <div>
      <div className="flex items-center gap-2">
        <span className="text-slate-400 text-xs w-24 shrink-0">{label}</span>
        <code className="flex-1 min-w-0 bg-slate-900 rounded px-2 py-1 text-slate-200 text-xs truncate">{value}</code>
        <button onClick={copy} className="text-slate-400 hover:text-white shrink-0">{copied ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}</button>
      </div>
      <p className="text-[11px] text-slate-500 mt-0.5 ml-26 pl-0 sm:pl-2">{hint}</p>
    </div>
  );
};

const RevealBox: React.FC<{ reveal: { name: string; api_key: string }; onClose: () => void }> = ({ reveal, onClose }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard?.writeText(reveal.api_key); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return (
    <div className="bg-amber-900/15 border border-amber-500/40 rounded-xl p-4 space-y-2">
      <p className="text-amber-200 font-semibold text-sm flex items-center gap-2"><AlertTriangle size={16} /> Copy your key now — it won't be shown again.</p>
      <div className="flex items-center gap-2">
        <code className="flex-1 min-w-0 bg-slate-950 rounded px-2 py-2 text-emerald-300 text-xs font-mono break-all">{reveal.api_key}</code>
        <button onClick={copy} className="shrink-0 bg-slate-800 hover:bg-slate-700 text-white px-3 py-2 rounded-lg text-sm flex items-center gap-1.5">{copied ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />} Copy</button>
      </div>
      <button onClick={onClose} className="text-xs text-slate-400 hover:text-white">I've saved it — dismiss</button>
    </div>
  );
};

export default ApiKeysView;
